import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { useAuthStore, ensureFreshToken } from "@/lib/store/authStore";
import { refreshCPToken } from "@/lib/api/controlPlaneClient";
import { usePageTranslation } from "@/i18n/useTranslation";
import { useLanguage } from "@/i18n/LanguageProvider";
import { sheetsTranslations } from "@/i18n/locales/en/sheets";
import { sheetsTranslations as thSheets } from "@/i18n/locales/th/sheets";
import { fetchUniverSso, UniverSsoError, UNIVER_ORIGIN } from "./fetchUniverSso";
import { pageContextStore } from "@/features/ai-context/pageContextStore";
import { useAiPanelStore } from "@/lib/store/aiPanelStore";
import {
  UniverErrorState,
  UniverLoadingState,
  UniverNoAccessState,
  UniverNotEnabledState,
} from "./UniverEmbedStates";

/** If the iframe hasn't fired `load` this long after we set its src, show the retry state. */
const IFRAME_LOAD_TIMEOUT_MS = 20_000;

/**
 * Posted by a zvgnext instance that finds itself rendered INSIDE the Sheets iframe.
 * That happens when the Univer fork's SSO fails and it 302s to its login URL
 * (https://work.zaviago.com/sheets, contract §3.5). The outer embed then shows the
 * error state instead of a dashboard-inside-a-dashboard (and the nested copy never
 * mints another token, so there's no SSO loop).
 */
const NESTED_SIGNAL = "ZVG_SHEETS_NESTED_IN_FRAME";

/** Posted by the Univer fork (P4) when its session is gone inside the frame. */
const LOGIN_REQUIRED_SIGNAL = "zaviago:sheets:login-required";
/** At most one automatic re-mint per interval; a second signal inside it shows the retry state. */
const AUTO_RELOGIN_MIN_INTERVAL_MS = 30_000;

/**
 * Posted by Zaviago Sheets (plan 1967 P6) with what the user has open: file, type, selection, lock.
 * Forwarded into the page-aware AI context (plan 1968) as page.extra["zaviago-sheets"].
 */
const CONTEXT_SIGNAL = "zaviago:sheets:context";
const AI_CONTEXT_SOURCE = "zaviago-sheets";
/**
 * Posted by a Zaviago Sheets dashboard's "ให้ AI สร้าง" quick start (plan 1969 F3/F8):
 * open the Zaviago AI panel next to the sheet. Its `prompt` is not used (the panel has no prefill).
 */
const ASK_AI_SIGNAL = "zaviago:sheets:ask-ai";
/** Hard cap on a forwarded context payload (the 1968 reporter budget is 8 KB for the whole page). */
const MAX_CONTEXT_BYTES = 4_096;

/** Only while the user is actually on /sheets — the persistent iframe keeps running on other pages. */
function onSheetsRoute(): boolean {
  return typeof window !== "undefined" && /^\/sheets(\/|$)/.test(window.location.pathname);
}

function forwardSheetsContext(data: unknown): void {
  if (!onSheetsRoute()) return;
  const extra = (data as { extra?: unknown } | null)?.extra;
  if (extra === null) {
    pageContextStore.getState().setExtra(AI_CONTEXT_SOURCE, null);
    return;
  }
  if (!extra || typeof extra !== "object" || Array.isArray(extra)) return;
  if ((extra as { app?: unknown }).app !== "zaviago-sheets") return;
  let size = 0;
  try {
    size = JSON.stringify(extra).length;
  } catch {
    return;
  }
  if (size > MAX_CONTEXT_BYTES) return;
  pageContextStore.getState().setExtra(AI_CONTEXT_SOURCE, extra as Record<string, unknown>);
}

function isFramed(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    return true; // cross-origin top → we are framed
  }
}

type Status = "loading" | "ready" | "error" | "not-enabled" | "no-access";

/** CP codes that mean "this store/user can't use Sheets" — calm state, no retry button. */
const NO_ACCESS_CODES = new Set(["TENANT_INACTIVE", "NOT_A_MEMBER", "TENANT_NOT_FOUND"]);

/**
 * Mint the SSO URL; on INVALID_TOKEN (CP answers 200, so cpGuestApiCall's 401 retry
 * doesn't fire) force ONE CP token refresh and try again.
 */
async function mintSsoUrl(tenant: string, email: string): Promise<string> {
  try {
    return (await fetchUniverSso(tenant, email)).url;
  } catch (err) {
    if (!(err instanceof UniverSsoError) || err.code !== "INVALID_TOKEN") throw err;
    const refreshed = await refreshCPToken();
    if (!refreshed) throw err;
    useAuthStore.setState({
      cpToken: refreshed.access_token,
      cpTokenExpiresAt: refreshed.expires_at,
    });
    return (await fetchUniverSso(tenant, email)).url;
  }
}

export interface UniverEmbedProps {
  className?: string;
  onReady?: () => void;
  onError?: (message: string) => void;
  /** Validated messages from the Univer origin (future: route sync). */
  onMessage?: (data: unknown) => void;
}

export interface UniverEmbedRef {
  isReady: boolean;
  reload: () => void;
}

/**
 * UniverEmbed — iframe wrapper for the self-hosted Univer Workspace (sheets.zaviago.com).
 *
 * Auth: Control Plane one-time-token SSO (contract §1/§4). Every mount and every retry
 * mints a FRESH URL (tokens are single-use, 5-min TTL). The tenant is derived by CP from
 * the user's JWT, never from the client.
 */
const UniverEmbed = forwardRef<UniverEmbedRef, UniverEmbedProps>(function UniverEmbed(
  { className, onReady, onError, onMessage },
  ref
) {
  const t = usePageTranslation({ en: sheetsTranslations, th: thSheets });
  const { language } = useLanguage();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const requestIdRef = useRef(0);
  const lastAutoReloginRef = useRef(0);
  const [status, setStatus] = useState<Status>("loading");
  const [iframeSrc, setIframeSrc] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const currentTenant = useAuthStore((state) => state.currentTenant);
  const username = useAuthStore((state) => state.username);

  const fail = useCallback(
    (message: string | null) => {
      setStatus("error");
      setErrorMsg(message);
      if (message) onError?.(message);
    },
    [onError]
  );

  const loadSso = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    const isStale = () => requestId !== requestIdRef.current;

    if (isFramed()) {
      window.parent.postMessage({ type: NESTED_SIGNAL }, window.location.origin);
      fail(null);
      return;
    }
    if (!currentTenant || !username) {
      fail(t.errorMissingIdentity);
      return;
    }

    setStatus("loading");
    setErrorMsg(null);
    try {
      // Avoid the token-refresh race (frappe-debug #62).
      await ensureFreshToken();
      const url = await mintSsoUrl(currentTenant, username);
      if (isStale()) return;
      // Zaviago Sheets reads ?lang= on the SSO entry and persists it per user (plan 1967 P1b).
      const withLang = new URL(url);
      withLang.searchParams.set("lang", language === "th" ? "th" : "en");
      setIframeSrc(withLang.toString());
    } catch (err) {
      if (isStale()) return;
      setIframeSrc(null);
      if (err instanceof UniverSsoError && err.code === "UNIVER_NOT_ENABLED") {
        setStatus("not-enabled");
        return;
      }
      if (err instanceof UniverSsoError && NO_ACCESS_CODES.has(err.code)) {
        setStatus("no-access");
        return;
      }
      console.error("[UniverEmbed] SSO failed:", err);
      fail(null);
    }
  }, [currentTenant, username, fail, t.errorMissingIdentity, language]);

  useEffect(() => {
    void loadSso();
    return () => {
      requestIdRef.current += 1; // invalidate in-flight request on unmount / identity change
    };
  }, [loadSso]);

  useImperativeHandle(
    ref,
    () => ({
      get isReady() {
        return status === "ready";
      },
      reload: () => void loadSso(),
    }),
    [status, loadSso]
  );

  // Cross-origin iframes rarely fire `error`, so time out a load that never completes.
  useEffect(() => {
    if (!iframeSrc || status !== "loading") return;
    const timer = window.setTimeout(() => fail(null), IFRAME_LOAD_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [iframeSrc, status, fail]);

  // postMessage: only from OUR iframe, and only from the exact Univer origin
  // (strict equality — never startsWith). Same-origin NESTED_SIGNAL = SSO bounced back to us.
  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      const frameWindow = iframeRef.current?.contentWindow;
      if (!frameWindow || event.source !== frameWindow) return;

      if (event.origin === UNIVER_ORIGIN) {
        // Univer session expired inside the frame → re-mint a fresh one-time SSO URL.
        // Throttled so a persistently failing SSO can never loop.
        if ((event.data as { type?: unknown } | null)?.type === CONTEXT_SIGNAL) {
          forwardSheetsContext(event.data);
          return;
        }
        if ((event.data as { type?: unknown } | null)?.type === ASK_AI_SIGNAL) {
          // Only for the visible Sheets page (the persistent iframe keeps running elsewhere).
          if (onSheetsRoute()) useAiPanelStore.getState().requestOpen();
          return;
        }
        if ((event.data as { type?: unknown } | null)?.type === LOGIN_REQUIRED_SIGNAL) {
          const now = Date.now();
          if (now - lastAutoReloginRef.current > AUTO_RELOGIN_MIN_INTERVAL_MS) {
            lastAutoReloginRef.current = now;
            void loadSso();
          } else {
            fail(null);
          }
          return;
        }
        onMessage?.(event.data);
        return;
      }
      if (
        event.origin === window.location.origin &&
        (event.data as { type?: unknown } | null)?.type === NESTED_SIGNAL
      ) {
        setIframeSrc(null);
        fail(null);
      }
    }
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [onMessage, fail, loadSso]);

  const handleIframeLoad = useCallback(() => {
    // A nested-dashboard bounce also fires `load`; its NESTED_SIGNAL flips us to error.
    setStatus((prev) => (prev === "loading" ? "ready" : prev));
    onReady?.();
  }, [onReady]);

  const handleRetry = useCallback(() => {
    void loadSso();
  }, [loadSso]);

  return (
    <div className={cn("relative h-full w-full bg-background", className)}>
      {status === "loading" && <UniverLoadingState t={t} />}
      {status === "error" && <UniverErrorState t={t} message={errorMsg} onRetry={handleRetry} />}
      {status === "not-enabled" && <UniverNotEnabledState t={t} />}
      {status === "no-access" && <UniverNoAccessState t={t} />}

      {/* Mounts only once we hold a fresh SSO URL. No sandbox: Univer needs scripts,
          storage, popups and downloads (xlsx export) — same as the other app embeds. */}
      {iframeSrc && (
        <iframe
          ref={iframeRef}
          src={iframeSrc}
          className="absolute inset-0 h-full w-full border-none"
          title={t.iframeTitle}
          allow="clipboard-read; clipboard-write; fullscreen"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
          onLoad={handleIframeLoad}
        />
      )}
    </div>
  );
});

export default UniverEmbed;
